import React, { useState, useMemo } from 'react';
import logo from '../kloudynet-logo.jpeg';

// ── Constants ─────────────────────────────────────────────────────────────────
const INDUSTRIES = ['Banking & Financial Services','Insurance','Healthcare & Life Sciences','Government & Public Sector','Energy & Utilities','Retail & E-commerce','Manufacturing','Telecommunications','Education','Professional Services','Technology','Other'];
const OFFICES = ['Malaysia','Singapore','Philippines','Thailand','India','UAE'];
const ENG_TYPES = ['New Assessment','Recertification (3 months)','Recertification (6 months)','Recertification (12 months)'];
const CONTACT_ROLES = ['Chief Information Security Officer (CISO)','Chief Information Officer (CIO)','Chief Technology Officer (CTO)','Head of Cybersecurity','Head of IT','Head of Risk & Compliance','Data Protection Officer (DPO)','AI/ML Lead','IT Manager','Other'];
const AI_SYSTEM_COUNTS = ['None yet — assessing readiness before adoption','1–5 pilots or proofs of concept','6–20 AI systems in production','21–50 AI systems in production','50+ AI systems in production'];
const ASSESSMENT_TRIGGERS = ['New AI deployment','Annual review','Regulatory requirement','Incident response','Board directive','MCI Workshop','Pre-sales qualification','Recertification','Third-party request','Other'];
const ENG_TIERS = [
  { value:'SAARA Express — 1–2 days (survey + auto-scan only)', tip:'Quick health check. Auto-scoring + self-assessment survey. No document review or interviews. Ideal for pre-sales qualification or fast readiness snapshot.' },
  { value:'SAARA Standard — 1 week (document review + interviews)', tip:'The default engagement. Auto-scoring + uploaded evidence review + 3–4 stakeholder interview sessions across all 6 domains. Full findings report with remediation roadmap.' },
  { value:'SAARA Enterprise — 3–6 weeks (deep evidence + full interview programme)', tip:'Large enterprise or regulated industry. Deep evidence collection, multiple interview rounds per domain, custom KQL queries, architecture review, and remediation workshop.' },
];
const DATA_REGIONS = ['Malaysia (Azure Southeast Asia)','Singapore (Azure Southeast Asia)','Indonesia','Thailand','Philippines','India (Azure Central India)','UAE (Azure UAE North)','EU (Azure West Europe)','US (Azure East US)','Customer\'s own Azure subscription (air-gapped)'];

// Country → regulatory jurisdiction suggestions
const COUNTRY_REGS = {
  'Malaysia':    ['BNM Risk in Technology Policy (RMiT)','PDPA Malaysia','Securities Commission Malaysia'],
  'Singapore':   ['MAS Technology Risk Management (TRM)','PDPC Singapore','MAS Outsourcing Guidelines'],
  'Indonesia':   ['OJK IT Risk Management','Indonesian PDP Law'],
  'Thailand':    ['BOT IT Examination','PDPA Thailand'],
  'Philippines': ['BSP Technology Risk Management','Data Privacy Act (Philippines)'],
  'India':       ['RBI IT Framework','DPDP Act India','SEBI Cybersecurity Framework'],
  'UAE':         ['CBUAE Cybersecurity Framework','DIFC Data Protection Law','UAE Personal Data Protection Law'],
  'EU':          ['EU AI Act','GDPR','DORA (financial entities)'],
  'UK':          ['FCA Operational Resilience','UK GDPR','ICO AI Guidance'],
};
const ALL_REGS = ['BNM Risk in Technology Policy (RMiT)','MAS Technology Risk Management (TRM)','OJK IT Risk Management','PDPA Malaysia','PDPC Singapore','Indonesian PDP Law','BOT IT Examination','PDPA Thailand','Data Privacy Act (Philippines)','RBI IT Framework','DPDP Act India','CBUAE Cybersecurity Framework','EU AI Act','GDPR','DORA','UK GDPR','Other / Not yet mapped'];

// Microsoft licensing options
const MS_LICENSING = ['Microsoft 365 E3','Microsoft 365 E5','Microsoft 365 Business Premium','Microsoft 365 Business Standard','Mixed (E3 + E5)','Other Microsoft licensing'];
// Google licensing options
const GOOGLE_LICENSING = ['Google Workspace Business Starter','Google Workspace Business Standard','Google Workspace Business Plus','Google Workspace Enterprise Standard','Google Workspace Enterprise Plus'];
// AWS options
const AWS_LICENSING = ['AWS Free Tier / Pay-as-you-go','AWS Business Support','AWS Enterprise Support','AWS GovCloud'];

// ── Identity & AI workload platform definitions ───────────────────────────────
const IDENTITY_PLATFORMS = [
  { id:'microsoft365', label:'Microsoft 365', desc:'Entra ID, Exchange, Teams, SharePoint, Intune', color:'#1565C0', bg:'#E8F0FE', autoScored:14, shadowAI:true },
  { id:'google',       label:'Google Workspace', desc:'Google Cloud Identity, Drive, Gmail, Meet', color:'#2E7D32', bg:'#E8F5E9', autoScored:0,  shadowAI:false },
  { id:'other_identity', label:'Other / On-premise', desc:'Okta, Active Directory, no cloud identity', color:'#455A64', bg:'#F1F3F5', autoScored:0,  shadowAI:false },
];

const AI_WORKLOADS = [
  { id:'azure_openai',  label:'Azure OpenAI',          desc:'Azure OpenAI, Microsoft Copilot, Agent 365',    color:'#1565C0', bg:'#E8F0FE', autoScored:0  },
  { id:'aws_bedrock',   label:'AWS Bedrock / SageMaker', desc:'Amazon Bedrock, SageMaker, AWS AI/ML',         color:'#E65100', bg:'#FFF3E0', autoScored:0  },
  { id:'google_vertex', label:'Google Vertex AI',       desc:'Vertex AI, Google AI Studio, Gemini API',       color:'#2E7D32', bg:'#E8F5E9', autoScored:0  },
  { id:'saas_ai',       label:'Third-party SaaS AI',    desc:'OpenAI API, Anthropic, Cohere, others',          color:'#6A1B9A', bg:'#F3E5F5', autoScored:0  },
  { id:'onprem_ai',     label:'On-premise / Private',   desc:'Self-hosted models, air-gapped environments',   color:'#455A64', bg:'#F1F3F5', autoScored:0  },
  { id:'none',          label:'None yet',               desc:'No AI workloads — assessing adoption readiness', color:'#B0BAC5', bg:'#F8F9FA', autoScored:0  },
];

function deriveStack(identityPlatforms, aiWorkloads) {
  const selIdentity = IDENTITY_PLATFORMS.filter(p => identityPlatforms.includes(p.id));
  const selAI       = AI_WORKLOADS.filter(w => aiWorkloads.includes(w.id));
  let autoScored = Math.min(
    selIdentity.reduce((a,p) => a+p.autoScored, 0) + selAI.reduce((a,w) => a+w.autoScored, 0),
    14
  );
  const hasShadowAI = selIdentity.some(p => p.shadowAI);
  const hasMicrosoft = identityPlatforms.includes('microsoft365') || aiWorkloads.includes('azure_openai');
  const hasGoogle    = (identityPlatforms.includes('google') || aiWorkloads.includes('google_vertex')) && !hasMicrosoft;
  const hasAWS       = aiWorkloads.includes('aws_bedrock') && !hasMicrosoft;
  const noCloud      = autoScored === 0;

  const stackLabel = hasMicrosoft ? 'Microsoft Stack'
    : hasGoogle ? 'Google / GCP Stack'
    : hasAWS    ? 'AWS Stack'
    : 'Manual Assessment Only';

  const permissions = [];
  if (hasMicrosoft || identityPlatforms.includes('microsoft365')) {
    permissions.push(
      { scope:'SecurityEvents.Read.All', purpose:'Security alerts and Secure Score from Defender', required:true },
      { scope:'Policy.Read.All', purpose:'Conditional Access policies and authentication settings', required:true },
      { scope:'Directory.Read.All', purpose:'Entra ID — users, groups, service principals', required:true },
      { scope:'AuditLog.Read.All', purpose:'Audit logs for governance and compliance evidence', required:true },
      { scope:'Reports.Read.All', purpose:'MFA registration and authentication method reports', required:true },
      { scope:'Log Analytics Reader (Azure RBAC)', purpose:'KQL queries against Sentinel workspace', required:hasMicrosoft },
      { scope:'Reader (Azure RBAC on subscription)', purpose:'Defender for Cloud and Defender for AI Services', required:hasMicrosoft },
    );
  }
  if (hasGoogle || identityPlatforms.includes('google')) {
    permissions.push(
      { scope:'Cloud Asset Viewer (GCP role)', purpose:'Read GCP resource configurations and IAM policies', required:true },
      { scope:'Security Reviewer (GCP role)', purpose:'Read Security Command Center findings', required:true },
      { scope:'Vertex AI Viewer', purpose:'Read Vertex AI model deployments and safety filters', required:aiWorkloads.includes('google_vertex') },
    );
  }
  if (hasAWS || aiWorkloads.includes('aws_bedrock')) {
    permissions.push(
      { scope:'IAM ReadOnlyAccess (AWS managed policy)', purpose:'Read IAM roles, policies, and identity configurations', required:true },
      { scope:'AmazonBedrockReadOnly', purpose:'Read Bedrock model access, guardrails, and agent configurations', required:true },
    );
  }

  let docHandling = null;
  if (identityPlatforms.includes('microsoft365')) {
    docHandling = { title:'Documents stay in SharePoint — read in-place via Graph API', color:'#1565C0', bg:'#E8F0FE', steps:['Create SharePoint library: SAARA-Evidence','Place governance documents using the naming guide provided','SAARA reads and scores in-place — no download to Kloudynet','Raw content discarded after scoring — only score + summary retained'] };
  } else if (identityPlatforms.includes('google')) {
    docHandling = { title:'Documents in a restricted Google Drive folder', color:'#2E7D32', bg:'#E8F5E9', steps:['Create Google Drive folder: SAARA Evidence','Share with SAARA service account (read-only)','Upload governance documents to the folder','Documents processed transiently — not stored by Kloudynet'] };
  } else if (aiWorkloads.includes('aws_bedrock')) {
    docHandling = { title:'Documents in a customer-controlled S3 bucket', color:'#E65100', bg:'#FFF3E0', steps:['Create S3 bucket: saara-evidence-{org-name} (private ACL)','Create scoped IAM role granting SAARA read-only access to that bucket','Upload governance documents','Documents retrieved transiently and discarded after scoring'] };
  } else {
    docHandling = { title:'Direct upload to SAARA portal', color:'#455A64', bg:'#F1F3F5', steps:['Assessor provides document checklist at engagement kickoff','Customer uploads documents via Document Uploads section','Documents processed by Azure OpenAI and discarded within 24 hours','For strict data residency: air-gapped deployment available'] };
  }

  const needsMSConsent  = hasMicrosoft || identityPlatforms.includes('microsoft365');
  const needsGCPAccount = hasGoogle    || identityPlatforms.includes('google') || aiWorkloads.includes('google_vertex');
  const needsAWSRole    = hasAWS       || aiWorkloads.includes('aws_bedrock');
  const noConnection    = !needsMSConsent && !needsGCPAccount && !needsAWSRole;

  return { autoScored, hasShadowAI, stackLabel, permissions, docHandling, needsMSConsent, needsGCPAccount, needsAWSRole, noConnection, selIdentity, selAI, isComplete: identityPlatforms.length > 0 && aiWorkloads.length > 0, noCloud };
}

// ── Sub-components ────────────────────────────────────────────────────────────
const Label = ({ children, required, hint }) => (
  <div style={{ marginBottom: 4 }}>
    <span style={{ fontSize: 11, fontWeight: 500, color: '#455A64' }}>{children}</span>
    {required && <span style={{ color: '#C62828' }}> *</span>}
    {hint && <span style={{ fontSize: 10, color: '#B0BAC5', marginLeft: 6 }}>{hint}</span>}
  </div>
);

const Err = ({ msg }) => msg ? <div style={{ fontSize: 10, color: '#C62828', marginTop: 3 }}>{msg}</div> : null;

const inputSx = (err) => ({
  fontFamily: 'Inter, sans-serif', fontSize: 12, background: '#fff', color: '#0D1B2A',
  border: `1px solid ${err ? '#C62828' : '#DDE1E6'}`, borderRadius: 6,
  padding: '7px 10px', width: '100%', transition: 'border-color 0.15s', boxSizing: 'border-box',
});

const SectionHeader = ({ title, sub, badge }) => (
  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom: 16 }}>
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A', marginBottom: 2 }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: '#7A8899' }}>{sub}</div>}
    </div>
    {badge && <span style={{ fontSize: 10, padding:'3px 9px', borderRadius: 10, background: '#E8F0FE', color: '#1565C0', fontWeight: 500, flexShrink:0, marginLeft:12 }}>{badge}</span>}
  </div>
);

const PlatformCard = ({ item, selected, onToggle }) => (
  <div onClick={onToggle} style={{
    border: `2px solid ${selected ? item.color : '#E0E4E8'}`,
    borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
    background: selected ? item.bg : '#fff', transition: 'all 0.15s', position: 'relative',
  }}>
    {selected && <div style={{ position:'absolute', top:6, right:8, width:16, height:16, borderRadius:'50%', background:item.color, color:'#fff', fontSize:9, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>✓</div>}
    <div style={{ fontSize: 12, fontWeight: 600, color: selected ? item.color : '#0D1B2A', marginBottom: 3, paddingRight: 18 }}>{item.label}</div>
    <div style={{ fontSize: 10, color: '#7A8899', lineHeight: 1.4, marginBottom: 6 }}>{item.desc}</div>
    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: selected ? item.color : '#F1F3F5', color: selected ? '#fff' : '#546E7A' }}>
      {item.autoScored > 0 ? `${item.autoScored} controls auto-scored` : 'Manual only'}
    </span>
  </div>
);

const MultiCheck = ({ options, selected, onToggle, columns = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 6 }}>
    {options.map(opt => (
      <div key={opt} onClick={() => onToggle(opt)} style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px',
        borderRadius: 6, cursor: 'pointer', fontSize: 11,
        background: selected.includes(opt) ? '#E8F0FE' : '#F8F9FA',
        border: `1px solid ${selected.includes(opt) ? '#1565C0' : '#E0E4E8'}`,
        transition: 'all 0.15s',
      }}>
        <div style={{ width:14, height:14, borderRadius:3, flexShrink:0, marginTop:1, background: selected.includes(opt)?'#1565C0':'#fff', border:`2px solid ${selected.includes(opt)?'#1565C0':'#C5D0DC'}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {selected.includes(opt) && <span style={{ color:'#fff', fontSize:8, fontWeight:700 }}>✓</span>}
        </div>
        <span style={{ color: selected.includes(opt) ? '#1565C0' : '#455A64', lineHeight: 1.4 }}>{opt}</span>
      </div>
    ))}
  </div>
);

const StepBadge = ({ n, label, step }) => (
  <div style={{
    display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20, fontSize:11, fontWeight:500,
    background: step===n ? '#1565C0' : step>n ? '#E8F5E9' : '#F5F7F9',
    border: `1px solid ${step===n ? '#1565C0' : step>n ? '#2E7D32' : '#E0E4E8'}`,
    color: step===n ? '#fff' : step>n ? '#2E7D32' : '#7A8899',
  }}>
    <span style={{ width:18, height:18, borderRadius:'50%', fontSize:10, display:'flex', alignItems:'center', justifyContent:'center', background: step===n?'rgba(255,255,255,0.2)':step>n?'#2E7D32':'#E0E4E8', color:step>n?'#fff':'inherit' }}>
      {step>n?'✓':n}
    </span>
    {label}
  </div>
);

const g2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 };
const g3 = { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 };

// ── Main component ────────────────────────────────────────────────────────────
export default function Initiate({ onBegin, existingEngagement }) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [connecting, setConnecting] = useState({});
  const [connected, setConnected] = useState({});
  const [consents, setConsents] = useState({ data:false, permissions:false, storage:false, cybersec:false });

  const [form, setForm] = useState({
    // Client profile
    orgName:'', industry:'', country:'', employees:'',
    contactName:'', contactEmail:'', contactRole:'',
    // Regulatory context
    regulatoryJurisdictions:[],
    aiSystemCount:'',
    assessmentTrigger:'',
    dataResidencyRequired: false,
    dataResidencyRegion:'',
    // Technology stack
    identityPlatforms:[],
    aiWorkloads:[],
    // Licensing — conditional
    microsoftLicensing:'',
    googleLicensing:'',
    awsLicensing:'',
    // Engagement configuration
    engName:'', engType:'New Assessment',
    assessor:'Abbas Ali Shaikh',
    engDate: new Date().toISOString().split('T')[0],
    office:'Malaysia',
    engTier:'SAARA Standard — 1 week (document review + interviews)',
    engTierTip:'',
    // Connection
    tenantId:'', gcpServiceAccount:'', awsArn:'',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));
  const toggleArr = (k, val) => setForm(p => ({
    ...p, [k]: p[k].includes(val) ? p[k].filter(x=>x!==val) : [...p[k], val]
  }));
  const toggleAIWorkload = (id) => {
    if (id==='none') { set('aiWorkloads', form.aiWorkloads.includes('none')?[]:['none']); return; }
    const without = form.aiWorkloads.filter(x=>x!=='none');
    set('aiWorkloads', without.includes(id) ? without.filter(x=>x!==id) : [...without, id]);
  };
  const toggleConsent = k => setConsents(p => ({...p,[k]:!p[k]}));

  const stack = useMemo(() => deriveStack(form.identityPlatforms, form.aiWorkloads), [form.identityPlatforms, form.aiWorkloads]);
  const suggestedRegs = COUNTRY_REGS[form.country] || [];

  const hasMSLicensing   = form.identityPlatforms.includes('microsoft365');
  const hasGoogleLicensing = form.identityPlatforms.includes('google');
  const hasAWSLicensing  = form.aiWorkloads.includes('aws_bedrock');

  const allConsented = Object.values(consents).every(Boolean);
  const allConnected = (
    (!stack.needsMSConsent  || connected.microsoft) &&
    (!stack.needsGCPAccount || connected.gcp) &&
    (!stack.needsAWSRole    || connected.aws) &&
    (stack.noConnection ? connected.manual : true)
  );

  const handleConnect = async (type) => {
    setConnecting(p=>({...p,[type]:true}));
    await new Promise(r=>setTimeout(r,2200));
    setConnecting(p=>({...p,[type]:false}));
    setConnected(p=>({...p,[type]:true}));
  };

  const validate1 = () => {
    const e = {};
    if (!form.orgName.trim())  e.orgName  = 'Required';
    if (!form.industry)        e.industry = 'Required';
    if (!form.country.trim())  e.country  = 'Required';
    if (!form.contactName.trim()) e.contactName = 'Required';
    if (!form.contactEmail.trim()||!form.contactEmail.includes('@')) e.contactEmail = 'Valid email required';
    if (!form.contactRole)     e.contactRole = 'Required';
    if (!form.aiSystemCount)   e.aiSystemCount = 'Required — this shapes interview depth and report framing';
    if (!form.assessmentTrigger) e.assessmentTrigger = 'Required — this determines executive summary tone';
    if (form.identityPlatforms.length===0) e.identityPlatforms = 'Select at least one identity platform';
    if (form.aiWorkloads.length===0)       e.aiWorkloads = 'Select at least one AI workload option';
    if (hasMSLicensing && !form.microsoftLicensing) e.microsoftLicensing = 'Required for Microsoft stack';
    if (!form.engName.trim()) e.engName = 'Required';
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleBegin = () => {
    onBegin({
      ...form,
      techStack: stack.stackLabel,
      autoScoredCount: stack.autoScored,
      startedAt: new Date().toISOString(),
    });
  };

  return (
    <div style={{width:'100%'}}>

      {/* Header */}
      <div className="report-header" style={{marginBottom:20}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <img src={logo} alt="Kloudynet" style={{width:36,height:36,borderRadius:4,objectFit:'contain'}}/>
          <div>
            <div className="report-header-title">New SAARA Engagement</div>
            <div className="report-header-meta">Secure AI Adoption Readiness Assessment &nbsp;·&nbsp; Kloudynet &nbsp;·&nbsp; Complete all steps to begin evidence collection</div>
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <StepBadge n={1} label="Engagement details" step={step}/>
          <StepBadge n={2} label="Consent & permissions" step={step}/>
          <StepBadge n={3} label="Connect & begin" step={step}/>
        </div>
      </div>

      {/* ── STEP 1 ─────────────────────────────────────────────────────────── */}
      {step===1 && (
        <div>

          {/* Section A — Client profile */}
          <div className="card" style={{marginBottom:14}}>
            <div className="card-header">
              <div className="card-header-title">A — Client profile</div>
              <div className="card-header-sub">Organization identity and primary contact</div>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={g2}>
                <div>
                  <Label required>Organization name</Label>
                  <input style={inputSx(errors.orgName)} value={form.orgName} onChange={e=>set('orgName',e.target.value)} placeholder="e.g. Contoso Financial Services"/>
                  <Err msg={errors.orgName}/>
                </div>
                <div>
                  <Label required>Industry sector</Label>
                  <select style={inputSx(errors.industry)} value={form.industry} onChange={e=>set('industry',e.target.value)}>
                    <option value="">Select industry</option>
                    {INDUSTRIES.map(i=><option key={i} value={i}>{i}</option>)}
                  </select>
                  <Err msg={errors.industry}/>
                </div>
              </div>
              <div style={g3}>
                <div>
                  <Label required>Country</Label>
                  <input style={inputSx(errors.country)} value={form.country} onChange={e=>{set('country',e.target.value); set('regulatoryJurisdictions',[]);}} placeholder="e.g. Malaysia"/>
                  <Err msg={errors.country}/>
                </div>
                <div>
                  <Label hint="optional">Number of employees</Label>
                  <input style={inputSx()} value={form.employees} onChange={e=>set('employees',e.target.value)} placeholder="e.g. 2,500"/>
                </div>
                <div>
                  <Label required>AI systems in production</Label>
                  <select style={inputSx(errors.aiSystemCount)} value={form.aiSystemCount} onChange={e=>set('aiSystemCount',e.target.value)}>
                    <option value="">Select count</option>
                    {AI_SYSTEM_COUNTS.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                  <Err msg={errors.aiSystemCount}/>
                </div>
              </div>
              <div style={g3}>
                <div>
                  <Label required>Primary contact name</Label>
                  <input style={inputSx(errors.contactName)} value={form.contactName} onChange={e=>set('contactName',e.target.value)} placeholder="e.g. John Smith"/>
                  <Err msg={errors.contactName}/>
                </div>
                <div>
                  <Label required>Role / title</Label>
                  <select style={inputSx(errors.contactRole)} value={form.contactRole} onChange={e=>set('contactRole',e.target.value)}>
                    <option value="">Select role</option>
                    {CONTACT_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                  <Err msg={errors.contactRole}/>
                </div>
                <div>
                  <Label required>Primary contact email</Label>
                  <input style={inputSx(errors.contactEmail)} value={form.contactEmail} onChange={e=>set('contactEmail',e.target.value)} placeholder="e.g. john.smith@contoso.com"/>
                  <Err msg={errors.contactEmail}/>
                </div>
              </div>
            </div>
          </div>

          {/* Section B — Regulatory context */}
          <div className="card" style={{marginBottom:14}}>
            <div className="card-header">
              <div className="card-header-title">B — Regulatory & risk context</div>
              <div className="card-header-sub">Determines which controls are P1 (regulatory breach risk) vs P2 (best practice) in the report</div>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <Label required>Assessment trigger — why is this assessment happening now?</Label>
                <select style={inputSx(errors.assessmentTrigger)} value={form.assessmentTrigger} onChange={e=>set('assessmentTrigger',e.target.value)}>
                  <option value="">Select trigger</option>
                  {ASSESSMENT_TRIGGERS.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                <Err msg={errors.assessmentTrigger}/>
              </div>
              <div>
                <Label hint="select all that apply">
                  Regulatory jurisdictions
                  {suggestedRegs.length>0 && <span style={{marginLeft:8,fontSize:10,color:'#1565C0',cursor:'pointer'}} onClick={()=>set('regulatoryJurisdictions',suggestedRegs)}>→ Apply {form.country} suggestions</span>}
                </Label>
                <MultiCheck options={ALL_REGS} selected={form.regulatoryJurisdictions} onToggle={v=>toggleArr('regulatoryJurisdictions',v)} columns={2}/>
              </div>
              <div style={g2}>
                <div>
                  <Label>Data residency requirement?</Label>
                  <div style={{display:'flex',gap:10,marginTop:4}}>
                    {['No — standard Kloudynet Azure (Southeast Asia)','Yes — specific region required'].map(opt=>(
                      <div key={opt} onClick={()=>set('dataResidencyRequired',opt.startsWith('Yes'))} style={{flex:1,padding:'8px 12px',borderRadius:6,cursor:'pointer',fontSize:11,border:`1px solid ${form.dataResidencyRequired===opt.startsWith('Yes')?'#1565C0':'#E0E4E8'}`,background:form.dataResidencyRequired===opt.startsWith('Yes')?'#E8F0FE':'#fff',color:form.dataResidencyRequired===opt.startsWith('Yes')?'#1565C0':'#455A64'}}>
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
                {form.dataResidencyRequired && (
                  <div>
                    <Label>Required data region</Label>
                    <select style={inputSx()} value={form.dataResidencyRegion} onChange={e=>set('dataResidencyRegion',e.target.value)}>
                      <option value="">Select region</option>
                      {DATA_REGIONS.map(r=><option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section C — Technology stack */}
          <div className="card" style={{marginBottom:14}}>
            <div className="card-header">
              <div className="card-header-title">C — Technology stack</div>
              <div className="card-header-sub">Select all that apply — determines permissions requested, auto-scored controls, and document handling approach</div>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:18}}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>1. Identity & collaboration platform <span style={{color:'#C62828'}}>*</span></div>
                <div style={{fontSize:11,color:'#7A8899',marginBottom:10}}>What do your users work in day to day? Determines governance, DLP, and identity controls.</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                  {IDENTITY_PLATFORMS.map(p=><PlatformCard key={p.id} item={p} selected={form.identityPlatforms.includes(p.id)} onToggle={()=>{const n=form.identityPlatforms.includes(p.id)?form.identityPlatforms.filter(x=>x!==p.id):[...form.identityPlatforms,p.id];set('identityPlatforms',n);}}/>)}
                </div>
                <Err msg={errors.identityPlatforms}/>
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>2. AI workload & infrastructure <span style={{color:'#C62828'}}>*</span></div>
                <div style={{fontSize:11,color:'#7A8899',marginBottom:10}}>Where do your AI models, APIs, and agents run? Determines infrastructure, model security, and monitoring controls.</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                  {AI_WORKLOADS.map(w=><PlatformCard key={w.id} item={w} selected={form.aiWorkloads.includes(w.id)} onToggle={()=>toggleAIWorkload(w.id)}/>)}
                </div>
                <Err msg={errors.aiWorkloads}/>
              </div>

              {/* Conditional licensing */}
              {(hasMSLicensing||hasGoogleLicensing||hasAWSLicensing) && (
                <div style={g3}>
                  {hasMSLicensing && (
                    <div>
                      <Label required>Microsoft licensing tier</Label>
                      <select style={inputSx(errors.microsoftLicensing)} value={form.microsoftLicensing} onChange={e=>set('microsoftLicensing',e.target.value)}>
                        <option value="">Select tier</option>
                        {MS_LICENSING.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                      <Err msg={errors.microsoftLicensing}/>
                    </div>
                  )}
                  {hasGoogleLicensing && (
                    <div>
                      <Label hint="optional">Google Workspace tier</Label>
                      <select style={inputSx()} value={form.googleLicensing} onChange={e=>set('googleLicensing',e.target.value)}>
                        <option value="">Select tier</option>
                        {GOOGLE_LICENSING.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                  )}
                  {hasAWSLicensing && (
                    <div>
                      <Label hint="optional">AWS support tier</Label>
                      <select style={inputSx()} value={form.awsLicensing} onChange={e=>set('awsLicensing',e.target.value)}>
                        <option value="">Select tier</option>
                        {AWS_LICENSING.map(l=><option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Live scoring preview */}
              {stack.isComplete && (
                <div style={{background:'#F5F7F9',border:'1px solid #E0E4E8',borderRadius:8,padding:14}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:10}}>Assessment scope for your environment</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
                    {[
                      {val:stack.autoScored, label:'Controls auto-scored', color:'#1565C0'},
                      {val:Math.max(0,47-stack.autoScored), label:'Via document review', color:'#6A1B9A'},
                      {val:8, label:'Assessor interviews only', color:'#455A64'},
                    ].map((s,i)=>(
                      <div key={i} style={{background:'#fff',border:'1px solid #E8ECF0',borderRadius:6,padding:'10px 12px',textAlign:'center'}}>
                        <div style={{fontSize:24,fontWeight:700,color:s.color}}>{s.val}</div>
                        <div style={{fontSize:10,color:'#7A8899'}}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {stack.selIdentity.map(p=><span key={p.id} className="pill" style={{background:p.bg,color:p.color}}>{p.label}</span>)}
                    {stack.selAI.map(w=><span key={w.id} className="pill" style={{background:w.bg,color:w.color}}>{w.label}</span>)}
                    {stack.hasShadowAI && <span className="pill pill-blue">✓ Shadow AI detection</span>}
                    {stack.noCloud && <span className="pill pill-gray">Manual assessment only</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section D — Engagement configuration */}
          <div className="card" style={{marginBottom:20}}>
            <div className="card-header">
              <div className="card-header-title">D — Engagement configuration</div>
              <div className="card-header-sub">Kloudynet internal — not shown to client</div>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={g2}>
                <div>
                  <Label required>Engagement name</Label>
                  <input style={inputSx(errors.engName)} value={form.engName} onChange={e=>set('engName',e.target.value)} placeholder="e.g. Contoso SAARA Assessment Q2 2026"/>
                  <Err msg={errors.engName}/>
                </div>
                <div>
                  <Label>Engagement type</Label>
                  <select style={inputSx()} value={form.engType} onChange={e=>set('engType',e.target.value)}>
                    {ENG_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label hint="internal only — sets assessor expectations and report depth">Engagement tier</Label>
                <div style={{display:'flex',flexDirection:'column',gap:6,marginTop:4}}>
                  {ENG_TIERS.map(t=>(
                    <div key={t.value} onClick={()=>set('engTier',t.value)} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'9px 12px',borderRadius:6,cursor:'pointer',fontSize:11,border:`1px solid ${form.engTier===t.value?'#1565C0':'#E0E4E8'}`,background:form.engTier===t.value?'#E8F0FE':'#fff',color:form.engTier===t.value?'#1565C0':'#455A64'}}>
                      <div style={{width:14,height:14,borderRadius:'50%',flexShrink:0,marginTop:1,border:`2px solid ${form.engTier===t.value?'#1565C0':'#C5D0DC'}`,background:form.engTier===t.value?'#1565C0':'#fff',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        {form.engTier===t.value&&<div style={{width:6,height:6,borderRadius:'50%',background:'#fff'}}/>}
                      </div>
                      <div>
                        <div>{t.value}</div>
                        <div style={{fontSize:10,color:'#7A8899',marginTop:3,lineHeight:1.4,fontWeight:400}}>{t.tip}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={g3}>
                <div>
                  <Label>Lead assessor</Label>
                  <input style={inputSx()} value={form.assessor} onChange={e=>set('assessor',e.target.value)}/>
                </div>
                <div>
                  <Label>Assessment date</Label>
                  <input type="date" style={inputSx()} value={form.engDate} onChange={e=>set('engDate',e.target.value)}/>
                </div>
                <div>
                  <Label>Kloudynet office</Label>
                  <select style={inputSx()} value={form.office} onChange={e=>set('office',e.target.value)}>
                    {OFFICES.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button className="btn-primary" onClick={()=>{if(validate1())setStep(2);}}>Next — Consent & permissions →</button>
          </div>
        </div>
      )}

      {/* ── STEP 2 ─────────────────────────────────────────────────────────── */}
      {step===2 && (
        <div>
          {/* Stack summary bar */}
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16,padding:'10px 14px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:8,flexWrap:'wrap'}}>
            <span style={{fontSize:11,color:'#7A8899'}}>Environment:</span>
            {stack.selIdentity.map(p=><span key={p.id} className="pill" style={{background:p.bg,color:p.color}}>{p.label}</span>)}
            {stack.selAI.map(w=><span key={w.id} className="pill" style={{background:w.bg,color:w.color}}>{w.label}</span>)}
            <span className="pill pill-blue">{stack.autoScored} controls auto-scored</span>
            {stack.hasShadowAI && <span className="pill pill-green">Shadow AI detection</span>}
            <button className="btn-ghost" style={{marginLeft:'auto',fontSize:11}} onClick={()=>setStep(1)}>← Change</button>
          </div>

          {/* What SAARA collects */}
          <div className="card" style={{marginBottom:14}}>
            <div className="card-header">
              <div className="card-header-title">What SAARA collects automatically for this environment</div>
              <div className="card-header-sub">{stack.autoScored>0?'Read-only API calls — no agent installation, no data leaving your environment':'No automated collection for this environment — document review and interviews only'}</div>
            </div>
            <div className="card-body">
              {stack.selIdentity.concat(stack.selAI).flatMap(p=>p.autoScored>0?p.collected||[]:[]).filter(Boolean).concat(
                stack.noCloud ? ['No automated collection — all 55 controls assessed via document review and stakeholder interviews'] : []
              ).map((item,i,arr)=>(
                <div key={i} style={{display:'flex',gap:10,padding:'7px 0',borderBottom:i<arr.length-1?'1px solid #F0F2F5':'none',fontSize:12,color:'#455A64',alignItems:'flex-start'}}>
                  <span style={{color:stack.autoScored>0?'#2E7D32':'#B0BAC5',fontWeight:600,flexShrink:0}}>{stack.autoScored>0?'✓':'○'}</span>
                  {item}
                </div>
              ))}
              <div className="info-box info-box-green" style={{marginTop:12}}>
                <strong>Never collected regardless of environment:</strong> No passwords or credentials · No email or message content · No financial records · No PII beyond directory metadata · Document content processed transiently — never stored by Kloudynet
              </div>
            </div>
          </div>

          {/* Document handling */}
          {stack.docHandling && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">{stack.docHandling.title}</div>
                <div className="card-header-sub">How policy documents and governance evidence are handled for this environment</div>
              </div>
              <div className="card-body">
                {stack.docHandling.steps.map((s,i)=>(
                  <div key={i} style={{display:'flex',gap:10,fontSize:12,color:'#455A64',alignItems:'flex-start',marginBottom:8}}>
                    <span style={{width:20,height:20,borderRadius:'50%',background:stack.docHandling.bg,color:stack.docHandling.color,fontSize:10,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{i+1}</span>
                    {s}
                  </div>
                ))}
                <div className="info-box info-box-amber" style={{marginTop:12}}>
                  <strong>Internal cybersecurity approval:</strong> This document handling approach can be reviewed by the client's security team before evidence collection begins. Kloudynet provides a formal data handling agreement on request.
                </div>
              </div>
            </div>
          )}

          {/* Permissions */}
          {stack.permissions.length>0 && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">API permissions required — scoped to selected platforms only</div>
                <div className="card-header-sub">Minimum read-only permissions. No write access requested. Revocable at any time.</div>
              </div>
              <div className="card-body" style={{padding:0}}>
                <table className="tbl">
                  <thead><tr><th style={{width:280}}>Permission scope</th><th>Purpose</th><th style={{width:80}}>Required</th></tr></thead>
                  <tbody>
                    {stack.permissions.map((p,i)=>(
                      <tr key={i}>
                        <td><span className="chip chip-ms" style={{fontFamily:'monospace',fontSize:10}}>{p.scope}</span></td>
                        <td style={{fontSize:11,color:'#455A64'}}>{p.purpose}</td>
                        <td>{p.required?<span className="pill pill-red">Required</span>:<span className="pill pill-gray">Optional</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {stack.permissions.length===0 && (
            <div className="info-box info-box-blue" style={{marginBottom:14}}>
              <strong>No API permissions required</strong> for the selected environment. Assessment proceeds entirely via document review and stakeholder interviews.
            </div>
          )}

          {/* Consent */}
          <div className="card" style={{marginBottom:20}}>
            <div className="card-header">
              <div className="card-header-title">Client consent and data handling agreement</div>
              <div className="card-header-sub">All four confirmations required before evidence collection begins</div>
            </div>
            <div className="card-body" style={{display:'flex',flexDirection:'column',gap:10}}>
              {[
                {key:'data',        label:'Data collection consent',         desc:`${form.orgName||'The client organization'} has reviewed the data collection scope above and authorizes Kloudynet Technologies to collect the listed evidence categories. Primary contact: ${form.contactName||'—'} (${form.contactRole||'—'}).`},
                {key:'permissions', label:`${stack.stackLabel} access permissions`, desc:stack.permissions.length>0?`The ${stack.permissions.filter(p=>p.required).length} required read-only permissions listed above will be granted before evidence collection runs. All permissions can be revoked at any time from the respective platform's admin console.`:'No automated API permissions are required for this environment.'},
                {key:'storage',     label:'Document handling consent',       desc:`${form.orgName||'The client'} agrees to the document handling approach described above. Document content is processed transiently and not retained by Kloudynet beyond the scoring operation.`},
                {key:'cybersec',    label:'Internal cybersecurity review',   desc:'The client confirms this assessment approach — including API permissions, data handling, and document evidence scope — has been reviewed by their internal information security team, or that such review is in progress and will be completed before automated evidence collection begins.'},
              ].map(c=>(
                <div key={c.key} onClick={()=>toggleConsent(c.key)} style={{display:'flex',gap:12,padding:'12px 14px',borderRadius:8,cursor:'pointer',background:consents[c.key]?'#E8F5E9':'#F8F9FA',border:`1px solid ${consents[c.key]?'#4CAF50':'#E0E4E8'}`,transition:'all 0.15s'}}>
                  <div style={{width:20,height:20,borderRadius:4,flexShrink:0,marginTop:1,background:consents[c.key]?'#2E7D32':'#fff',border:`2px solid ${consents[c.key]?'#2E7D32':'#C5D0DC'}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    {consents[c.key]&&<span style={{color:'#fff',fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:'#0D1B2A',marginBottom:3}}>{c.label}</div>
                    <div style={{fontSize:11,color:'#546E7A',lineHeight:1.5}}>{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{display:'flex',justifyContent:'space-between'}}>
            <button className="btn-secondary" onClick={()=>setStep(1)}>← Back</button>
            <button className="btn-primary" disabled={!allConsented} style={{opacity:allConsented?1:0.5}} onClick={()=>setStep(3)}>Next — Connect & begin →</button>
          </div>
        </div>
      )}

      {/* ── STEP 3 ─────────────────────────────────────────────────────────── */}
      {step===3 && (
        <div>
          {stack.needsMSConsent && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">Connect Microsoft tenant</div>
                <div className="card-header-sub">One-time OAuth admin consent — runs in Kloudynet's Azure subscription at no cost to the customer. Revocable at any time from Entra ID admin center.</div>
              </div>
              <div className="card-body">
                <div style={{display:'flex',gap:10,marginBottom:8}}>
                  <input style={{...inputSx(),flex:1}} value={form.tenantId} onChange={e=>set('tenantId',e.target.value)} placeholder="e.g. contoso.onmicrosoft.com or tenant GUID"/>
                  <button className={connected.microsoft?'btn-secondary':'btn-primary'} style={{whiteSpace:'nowrap',minWidth:170}} onClick={()=>handleConnect('microsoft')} disabled={connecting.microsoft||connected.microsoft}>
                    {connecting.microsoft?'Connecting...':connected.microsoft?'✓ Connected':'Grant admin consent'}
                  </button>
                </div>
                <div style={{fontSize:10,color:'#7A8899'}}>Opens: login.microsoftonline.com/{'{tenant}'}/adminconsent?client_id={'{saara-app-id}'}</div>
                {connected.microsoft && <div className="info-box info-box-green" style={{marginTop:10}}>✓ Microsoft tenant connected. Read-only consent granted.</div>}
              </div>
            </div>
          )}

          {stack.needsGCPAccount && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">Connect Google Cloud / Workspace</div>
                <div className="card-header-sub">Customer creates a service account with read-only roles. Key stored in Azure Key Vault with restricted access.</div>
              </div>
              <div className="card-body">
                <div style={{display:'flex',gap:10,marginBottom:8}}>
                  <input style={{...inputSx(),flex:1}} value={form.gcpServiceAccount} onChange={e=>set('gcpServiceAccount',e.target.value)} placeholder="e.g. saara-reader@contoso.iam.gserviceaccount.com"/>
                  <button className={connected.gcp?'btn-secondary':'btn-primary'} style={{whiteSpace:'nowrap',minWidth:170}} onClick={()=>handleConnect('gcp')} disabled={connecting.gcp||connected.gcp}>
                    {connecting.gcp?'Validating...':connected.gcp?'✓ Connected':'Validate service account'}
                  </button>
                </div>
                {connected.gcp && <div className="info-box info-box-green" style={{marginTop:10}}>✓ GCP service account validated.</div>}
              </div>
            </div>
          )}

          {stack.needsAWSRole && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">Connect AWS environment</div>
                <div className="card-header-sub">Customer creates a cross-account IAM role. SAARA uses STS AssumeRole — no credentials stored.</div>
              </div>
              <div className="card-body">
                <div style={{display:'flex',gap:10,marginBottom:8}}>
                  <input style={{...inputSx(),flex:1}} value={form.awsArn} onChange={e=>set('awsArn',e.target.value)} placeholder="e.g. arn:aws:iam::123456789012:role/SAARA-ReadOnly"/>
                  <button className={connected.aws?'btn-secondary':'btn-primary'} style={{whiteSpace:'nowrap',minWidth:170}} onClick={()=>handleConnect('aws')} disabled={connecting.aws||connected.aws}>
                    {connecting.aws?'Validating...':connected.aws?'✓ Connected':'Validate IAM role'}
                  </button>
                </div>
                {connected.aws && <div className="info-box info-box-green" style={{marginTop:10}}>✓ AWS IAM role validated.</div>}
              </div>
            </div>
          )}

          {stack.noConnection && (
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <div className="card-header-title">No automated connection required</div>
                <div className="card-header-sub">Manual assessment — document review and interviews only</div>
              </div>
              <div className="card-body">
                <div className="info-box info-box-blue" style={{marginBottom:12}}>No API connections are needed. Assessment proceeds via document uploads and stakeholder interviews.</div>
                <button className="btn-primary" onClick={()=>setConnected({manual:true})}>Confirm — proceed with manual assessment</button>
                {connected.manual && <div className="info-box info-box-green" style={{marginTop:10}}>✓ Manual assessment confirmed.</div>}
              </div>
            </div>
          )}

          {/* Engagement summary */}
          <div className="card" style={{marginBottom:20}}>
            <div className="card-header">
              <div className="card-header-title">Engagement summary — review before beginning</div>
            </div>
            <div className="card-body" style={{padding:0}}>
              <table className="tbl">
                <tbody>
                  {[
                    ['Client', form.orgName],
                    ['Industry', form.industry],
                    ['Country', form.country],
                    ['Primary contact', `${form.contactName} (${form.contactRole}) — ${form.contactEmail}`],
                    ['AI systems in production', form.aiSystemCount],
                    ['Assessment trigger', form.assessmentTrigger],
                    ['Regulatory jurisdictions', form.regulatoryJurisdictions.join(', ')||'Not specified'],
                    ['Data residency', form.dataResidencyRequired ? (form.dataResidencyRegion||'Yes — region TBC') : 'Standard (Azure Southeast Asia)'],
                    ['Identity platform', stack.selIdentity.map(p=>p.label).join(', ')||'—'],
                    ['AI workload platform', stack.selAI.map(w=>w.label).join(', ')||'—'],
                    form.microsoftLicensing && ['Microsoft licensing', form.microsoftLicensing],
                    form.googleLicensing   && ['Google Workspace tier', form.googleLicensing],
                    ['Controls auto-scored', `${stack.autoScored} of 55`],
                    ['Shadow AI detection', stack.hasShadowAI?'✓ Included':'Not available for this environment'],
                    ['Engagement name', form.engName],
                    ['Engagement type', form.engType],
                    ['Engagement tier', form.engTier],
                    ['Lead assessor', form.assessor],
                    ['Assessment date', form.engDate],
                    ['Kloudynet office', form.office],
                  ].filter(Boolean).map(([k,v])=>(
                    <tr key={k}>
                      <td style={{width:220,fontWeight:500,color:'#455A64',fontSize:12}}>{k}</td>
                      <td style={{fontSize:12,color:'#0D1B2A'}}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <button className="btn-secondary" onClick={()=>setStep(2)}>← Back</button>
            <button className="btn-primary"
              disabled={!allConnected && !connected.manual}
              style={{opacity:(allConnected||connected.manual)?1:0.5,padding:'10px 24px',fontSize:13}}
              onClick={handleBegin}>
              Begin assessment →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
